import { useQuery, useQueryClient } from "react-query";
import { defaultStaleTime } from "../withQueryData";
import { usePropertiesApi } from "./usePropertiesApi";

const queryKeys = {
    allAlbums: (propertyId) => ['propertyPictures', { propertyId }],
    album: (propertyId, albumId) => ['propertyPictures', { propertyId, albumId }],
};

/**
 * Hook for managing property pictures albums.
 *
 * When albumId is provided, fetches a single album — checking the all-albums cache
 * first as initialData to avoid an extra network round-trip.
 *
 * When albumId is omitted (or null), fetches all albums for the property and
 * populates individual album caches from the result.
 *
 * Mutation functions (create/upload/update/delete) keep both the all-albums cache
 * and individual album caches in sync so downstream queries never need to refetch.
 *
 * @param {string|null} propertyId - Property identifier
 * @param {string|null} [albumId]  - Album identifier; when provided, fetches only this album
 * @param {boolean}     [enabled]  - Allows callers to disable the query (default: true)
 */
export const usePropertyPicturesData = (propertyId, albumId = null, enabled = true) => {
    const {
        getPictures,
        getPicturesAlbum,
        createPicturesAlbum,
        uploadPictures,
        updatePicturesAlbum,
        deletePicturesAlbum,
        deletePictures,
    } = usePropertiesApi();

    const queryClient = useQueryClient();

    // When fetching a single album, seed it from the all-albums cache to avoid
    // a network request when the list has already been loaded.
    const cachedAllAlbums = queryClient.getQueryData(queryKeys.allAlbums(propertyId));

    const picturesQueryResults = useQuery({
        queryKey: albumId
            ? queryKeys.album(propertyId, albumId)
            : queryKeys.allAlbums(propertyId),
        queryFn: albumId
            ? () => getPicturesAlbum(propertyId, albumId)
            : () => getPictures(propertyId),
        initialData: albumId
            ? () => cachedAllAlbums?.find(a => a.id === albumId)
            : undefined,
        staleTime: defaultStaleTime,
        enabled: enabled && !!propertyId,
    });

    // Helpers to keep both cache levels in sync after any mutation.

    const setAlbumInCache = (updatedAlbum) => {
        queryClient.setQueryData(queryKeys.album(propertyId, updatedAlbum.id), updatedAlbum);

        const cached = queryClient.getQueryData(queryKeys.allAlbums(propertyId));
        if (cached) {
            queryClient.setQueryData(
                queryKeys.allAlbums(propertyId),
                cached.map(a => a.id === updatedAlbum.id ? updatedAlbum : a)
            );
        }
    };

    const _createPicturesAlbum = async (albumId, metadata) => {
        const createdAlbum = await createPicturesAlbum(propertyId, albumId, metadata);

        queryClient.setQueryData(queryKeys.album(propertyId, createdAlbum.id), createdAlbum);

        const cached = queryClient.getQueryData(queryKeys.allAlbums(propertyId));
        if (cached) {
            queryClient.setQueryData(queryKeys.allAlbums(propertyId), [...cached, createdAlbum]);
        }

        return createdAlbum;
    };

    const _uploadPictures = async (albumId, files) => {
        const updatedAlbum = await uploadPictures(propertyId, albumId, files);
        if (updatedAlbum) setAlbumInCache(updatedAlbum);
        return updatedAlbum;
    };

    const _updatePicturesAlbum = async (albumId, metadata) => {
        const updatedAlbum = await updatePicturesAlbum(propertyId, albumId, metadata);
        if (updatedAlbum) setAlbumInCache(updatedAlbum);
        return updatedAlbum;
    };

    const _deletePicturesAlbum = async (albumId) => {
        await deletePicturesAlbum(propertyId, albumId);

        queryClient.removeQueries(queryKeys.album(propertyId, albumId));

        const cached = queryClient.getQueryData(queryKeys.allAlbums(propertyId));
        if (cached) {
            queryClient.setQueryData(
                queryKeys.allAlbums(propertyId),
                cached.filter(a => a.id !== albumId)
            );
        }
    };

    const _deletePictures = async (albumId, fileNames) => {
        const updatedAlbum = await deletePictures(propertyId, albumId, fileNames);
        if (updatedAlbum) setAlbumInCache(updatedAlbum);
        return updatedAlbum;
    };

    return {
        picturesQueryResults,
        createPicturesAlbum: _createPicturesAlbum,
        uploadPictures: _uploadPictures,
        updatePicturesAlbum: _updatePicturesAlbum,
        deletePicturesAlbum: _deletePicturesAlbum,
        deletePictures: _deletePictures,
    };
};
